// Authentication Configuration for Okta and Microsoft SSO
//
// During the Okta tenant migration the app straddles two Okta tenants at once:
//   - primary: where the in-app "Sign in with Okta" button sends users — the NEW tenant.
//   - idp:     the OLD tenant. No login starts there any more; it is reached only when Okta
//     itself launches the app from that tenant's tile, hitting our Initiate Login URI
//     (/okta/login) with an `iss` param, and we run the code+PKCE flow against that tenant.
//
// The franchise backend is dual-trust (accepts tokens from either issuer), so a session from
// either path is valid. A user is only ever logged in through one tenant at a time; we record
// which one so session restore, the OAuth callback, and logout use the matching instance.
import { OktaAuth } from '@okta/okta-auth-js';

export type TenantKey = 'primary' | 'idp';

const TENANTS: Record<TenantKey, { issuer: string; clientId: string }> = {
  primary: {
    issuer: import.meta.env.VITE_OKTA_ISSUER || '',
    clientId: import.meta.env.VITE_OKTA_CLIENT_ID || '',
  },
  idp: {
    // Optional: unset until the new tenant's app is wired up, which makes tenantForIssuer()
    // never match it and leaves the IdP path a no-op.
    issuer: import.meta.env.VITE_OKTA_ISSUER_IDP || '',
    clientId: import.meta.env.VITE_OKTA_CLIENT_ID_IDP || '',
  },
};

const TENANT_KEYS: TenantKey[] = ['primary', 'idp'];

// Records which tenant the active session was created against. Has to survive the OAuth
// redirect, hence localStorage, and defaults to primary — which is also correct for any
// session that predates this change.
const LOGIN_TENANT_KEY = 'okta_login_tenant';

function buildConfig(tenant: TenantKey) {
  const { issuer, clientId } = TENANTS[tenant];
  return {
    clientId,
    issuer,
    redirectUri: window.location.origin + '/login/callback',
    // `offline_access` is what makes Okta issue a refresh token, and the access token it
    // renews lives 60 minutes. Without one, okta-auth-js can only renew through a hidden
    // iframe against the Okta session cookie — a cross-site cookie browsers block — so an
    // hour into a session the app holds nothing it can authenticate a request with. The Okta
    // client already has the refresh_token grant, so the scope is the whole of what is needed.
    //
    // Only new logins ask for it; a session established before this was added has no refresh
    // token, and getV2AuthHeaders recovers those interactively.
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    pkce: true,
    dpop: false,  // Disable DPoP
    tokenManager: {
      storage: 'localStorage',
      // The primary tenant keeps the default 'okta-token-storage' key so sessions that
      // predate this change survive the deploy. The idp tenant gets its own key so a
      // restored session never validates one tenant's token against the other's issuer.
      ...(tenant === 'primary' ? {} : { storageKey: `okta-token-storage-${tenant}` }),
    },
  };
}

export function isTenantConfigured(tenant: TenantKey): boolean {
  return !!(TENANTS[tenant].issuer && TENANTS[tenant].clientId);
}

// Instances are built lazily, both because the normal (primary) path should never construct
// the idp instance and because the constructor throws when `issuer` is missing — which would
// crash module load in CI/test builds where the Okta env vars aren't configured. The
// AuthProvider's test-mode bypass returns before touching any of this.
const instances: Partial<Record<TenantKey, OktaAuth>> = {};

export function oktaAuthFor(tenant: TenantKey): OktaAuth {
  let inst = instances[tenant];
  if (!inst) {
    inst = new OktaAuth(buildConfig(tenant));
    inst.start();
    instances[tenant] = inst;
  }
  return inst;
}

export function getLoginTenant(): TenantKey {
  try {
    return localStorage.getItem(LOGIN_TENANT_KEY) === 'idp' ? 'idp' : 'primary';
  } catch {
    return 'primary';
  }
}

export function setLoginTenant(tenant: TenantKey): void {
  try {
    localStorage.setItem(LOGIN_TENANT_KEY, tenant);
  } catch {
    // Ignore storage errors — falls back to the primary default.
  }
}

export function clearLoginTenant(): void {
  try {
    localStorage.removeItem(LOGIN_TENANT_KEY);
  } catch {
    // Ignore storage errors.
  }
}

// The OktaAuth instance for the active session — used for session restore, the OAuth
// callback, and logout, all of which must act on the tenant that issued the tokens.
export function getActiveOktaAuth(): OktaAuth {
  return oktaAuthFor(getLoginTenant());
}

// Both token stores can hold live tokens at once (button login, then later the new tenant's
// dashboard tile), so anything that decides the session is dead has to empty both — a
// leftover set under the other key would resurrect a session the app just discarded.
// `except` exists for logout, which needs the active tenant's idToken intact so signOut() can
// pass id_token_hint and actually end the session Okta-side.
export function clearOktaTokens(except?: TenantKey): void {
  for (const tenant of TENANT_KEYS) {
    if (tenant === except || !isTenantConfigured(tenant)) continue;
    oktaAuthFor(tenant).tokenManager.clear();
  }
}

// The scheme + host of an issuer URL, i.e. the Okta org that hosts the authorization server.
// Returns null for anything that isn't an absolute http(s) URL, so a malformed config value
// can't produce a match.
function orgUrl(issuer: string): string | null {
  const match = /^(https?:\/\/[^/]+)/.exec(issuer);
  return match ? match[1] : null;
}

// Maps an Okta-supplied `iss` (from the Initiate Login URI) to a configured tenant, or null
// if it matches none. Trailing-slash tolerant.
//
// Two forms have to match, because the same tenant identifies itself differently depending on
// who is talking:
//   - the org URL (`https://weinsure.okta.com`) — what Okta sends as `iss` on an IdP-initiated
//     launch. An OIDC app isn't bound to an authorization server (the client picks that by
//     choosing an /authorize URL), so Okta has no /oauth2/default form to send there. Matching
//     only the configured issuer silently sends every tile launch to the primary tenant, which
//     is indistinguishable from the tile being misconfigured.
//   - the issuer itself (`…/oauth2/default`) — the `iss` claim in the tokens that server mints.
// Both are unambiguous: the two tenants are different hosts.
export function tenantForIssuer(iss: string | null | undefined): TenantKey | null {
  if (!iss) return null;
  const norm = (s: string) => s.replace(/\/+$/, '');
  const target = norm(iss);
  const matches = (tenant: TenantKey) => {
    const issuer = TENANTS[tenant].issuer;
    if (!issuer) return false;
    return norm(issuer) === target || orgUrl(norm(issuer)) === target;
  };
  if (matches('idp')) return 'idp';
  if (matches('primary')) return 'primary';
  return null;
}

// Resolves to the active tenant's instance on every property access, so the many call sites
// that just want "the current session's Okta client" need no tenant awareness. Anything that
// *starts* a login must use oktaAuthFor(tenant) with a concrete tenant instead, since the
// marker this reads is only correct once that choice has been recorded.
export const oktaAuth = new Proxy({} as OktaAuth, {
  get(_target, prop) {
    return (getActiveOktaAuth() as unknown as Record<string | symbol, unknown>)[prop];
  },
});

export const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
    authority: import.meta.env.VITE_MICROSOFT_AUTHORITY || 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin + '/microsoft/callback',
    // Don't let MSAL navigate the frame back to the original login-request URL after it
    // processes a response. During silent renewal the response lands in a hidden iframe
    // still showing login.microsoftonline.com, and that cross-origin navigation is blocked
    // by the browser ("unsafe attempt to initiate navigation"). Our /microsoft/callback
    // component already routes the user after a real redirect login, so MSAL's own
    // navigation is redundant here.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    temporaryCacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

// `offline_access` is required for MSAL to receive a refresh token; without it, silent
// token renewal falls back to a hidden iframe against the Entra session cookie, which
// browsers (and localhost) increasingly block — so acquireTokenSilent yields nothing and
// the backend sees no token. With it, acquireTokenSilent can mint fresh ID tokens for the
// refresh-token lifetime without any iframe/cookie dependency.
export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email', 'offline_access'],
};

// Scopes for silent token acquisition on API calls (identity + refresh only; the backend
// verifies the ID token's audience, so no resource scope is needed here).
export const silentTokenRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access'],
};
